/**
 * Report PDF Preview Modal.
 *
 * Renders the same layout as the PDF worker report template.
 * Single continuous page with all 13 report sections.
 * Pattern follows ConsultationPreviewModal.
 *
 * PII NOTE: patient name shown as "[Patient Name]" placeholder (ADR-0009).
 */

import { Modal } from "@hiya/ui";
import { HORMONE_LABELS, type HormoneCard } from "../../../types/report";

interface PreviewProps {
  open: boolean;
  onClose: () => void;
  kitCode: string;
  productType: string;
  cyclePhase: string;
  testDate: string;
  patientAge: number | null;
  cycleContext: { phase: string; doctor_context: string } | null;
  hormoneCards: HormoneCard[];
  cycleSummary: string;
  nonCycleSummary: string;
  finalAssessment: string;
  planNextSteps: string;
  doctorName: string | null;
  signatureUrl: string | null;
}

const HEADER_BG = "#F2D5BB";
const HEADER_FG = "#A66046";
const PAGE_WIDTH = 595;
const SCALE = 0.75;

function PageHeader({ title, subtitle }: Readonly<{ title: string; subtitle: string }>) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: HEADER_BG,
        padding: 24,
      }}
    >
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: HEADER_FG, lineHeight: 1.2 }}>
          {title}
        </div>
        <div style={{ fontSize: 16, fontWeight: 500, color: HEADER_FG, lineHeight: 1.5 }}>
          {subtitle}
        </div>
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, color: HEADER_FG, letterSpacing: 3 }}>
        HIYA
      </div>
    </div>
  );
}

function SectionBlock({
  title,
  children,
}: Readonly<{
  title: string;
  children: React.ReactNode;
}>) {
  return (
    <div style={{ padding: "12px 24px" }}>
      <div style={{ fontSize: 16, fontWeight: 600, color: "#1C1C1C", marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.5, color: "#1C1C1C" }}>
        {children}
      </div>
    </div>
  );
}

function HormoneCardPreview({ card }: Readonly<{ card: HormoneCard }>) {
  const statusColor = card.status === "high" ? "#A66046" : card.status === "low" ? "#A68263" : "#1C1C1C";

  return (
    <div
      style={{
        background: "#FBFBFB",
        border: "1px solid #F4F4F4",
        borderRadius: 12,
        padding: 16,
        marginBottom: 8,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontWeight: 600 }}>{HORMONE_LABELS[card.hormone_code] ?? card.hormone_code}</span>
        <span style={{ color: statusColor, fontWeight: 500, textTransform: "capitalize" }}>{card.status}</span>
      </div>
      <div style={{ display: "flex", gap: 16 }}>
        <div>
          <span style={{ fontSize: 12, color: "#A4A4A4" }}>Value</span>
          <div style={{ color: statusColor, fontWeight: 500 }}>{card.value} {card.unit}</div>
        </div>
        <div>
          <span style={{ fontSize: 12, color: "#A4A4A4" }}>Range</span>
          <div>{card.reference_range}</div>
        </div>
      </div>
      {card.general_explanation && (
        <div style={{ marginTop: 8, fontSize: 13, color: "#666" }}>{card.general_explanation}</div>
      )}
      {card.patient_interpretation && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#1C1C1C", marginBottom: 2 }}>Case-Specific Output</div>
          <div style={{ fontSize: 13, color: "#666" }}>{card.patient_interpretation}</div>
        </div>
      )}
    </div>
  );
}

export function ReportPreviewModal({
  open,
  onClose,
  kitCode,
  productType,
  cyclePhase,
  testDate,
  patientAge,
  cycleContext,
  hormoneCards,
  cycleSummary,
  nonCycleSummary,
  finalAssessment,
  planNextSteps,
  doctorName,
  signatureUrl,
}: Readonly<PreviewProps>) {
  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title="Report Preview">
      <div
        style={{
          maxHeight: "80vh",
          overflowY: "auto",
          padding: "24px 0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          background: "#f0f0f0",
        }}
      >
        <div
          style={{
            width: PAGE_WIDTH,
            background: "#fff",
            fontFamily: '"Lato", sans-serif',
            fontSize: 14,
            color: "#1C1C1C",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 2px 16px rgba(0,0,0,0.12)",
            borderRadius: 4,
            overflow: "visible",
            transform: `scale(${SCALE})`,
            transformOrigin: "top center",
          }}
        >
          <div style={{ flex: 1 }}>
            <PageHeader title="Hormone Report" subtitle={productType} />

            {/* Patient info card */}
            <div
              style={{
                margin: "16px 24px",
                background: "#FBFBFB",
                border: "1px solid #F4F4F4",
                borderRadius: 16,
                padding: 20,
              }}
            >
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <div style={{ fontSize: 12, color: "#A4A4A4", marginBottom: 4 }}>Patient</div>
                  <div style={{ fontWeight: 500 }}>[Patient Name]</div>
                </div>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <div style={{ fontSize: 12, color: "#A4A4A4", marginBottom: 4 }}>Kit Code</div>
                  <div style={{ fontWeight: 500 }}>{kitCode}</div>
                </div>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <div style={{ fontSize: 12, color: "#A4A4A4", marginBottom: 4 }}>Test Date</div>
                  <div style={{ fontWeight: 500 }}>{testDate}</div>
                </div>
                {patientAge != null && (
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <div style={{ fontSize: 12, color: "#A4A4A4", marginBottom: 4 }}>Age</div>
                    <div style={{ fontWeight: 500 }}>{patientAge} years</div>
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 120 }}>
                  <div style={{ fontSize: 12, color: "#A4A4A4", marginBottom: 4 }}>Cycle Phase</div>
                  <div style={{ fontWeight: 500 }}>{cyclePhase}</div>
                </div>
              </div>
            </div>

            {/* Cycle Context */}
            {cycleContext && (
              <SectionBlock title="Cycle Context">
                <p style={{ fontWeight: 500, marginBottom: 4 }}>{cycleContext.phase}</p>
                <p>{cycleContext.doctor_context}</p>
              </SectionBlock>
            )}

            {/* Hormone Cards */}
            {hormoneCards.length > 0 && (
              <SectionBlock title="Hormone Results">
                {hormoneCards.map((card) => (
                  <HormoneCardPreview key={card.hormone_code} card={card} />
                ))}
              </SectionBlock>
            )}

            {/* Cycle Summary */}
            {cycleSummary && (
              <SectionBlock title="Cycle Summary">
                <p style={{ whiteSpace: "pre-wrap" }}>{cycleSummary}</p>
              </SectionBlock>
            )}

            {/* Non-cycle Summary */}
            {nonCycleSummary && (
              <SectionBlock title="Non-Cycle Summary">
                <p style={{ whiteSpace: "pre-wrap" }}>{nonCycleSummary}</p>
              </SectionBlock>
            )}

            {/* Final Assessment */}
            {finalAssessment && (
              <SectionBlock title="Final Assessment">
                <p style={{ whiteSpace: "pre-wrap" }}>{finalAssessment}</p>
              </SectionBlock>
            )}

            {/* Plan / Next Steps */}
            {planNextSteps && (
              <SectionBlock title="Plan / Next Steps">
                <p style={{ whiteSpace: "pre-wrap" }}>{planNextSteps}</p>
              </SectionBlock>
            )}

            {/* Signature */}
            {doctorName && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px" }}>
                <div>
                  <div style={{ fontSize: 14, color: "#1C1C1C", lineHeight: 1.5 }}>Kindest regards,</div>
                  <div style={{ fontSize: 14, color: "#A4A4A4", lineHeight: 1.5 }}>{doctorName}</div>
                </div>
                {signatureUrl && (
                  <img
                    src={signatureUrl}
                    alt="Doctor signature"
                    style={{ height: 80, width: 120, objectFit: "contain" }}
                  />
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: HEADER_BG,
              padding: "12px 24px",
              fontSize: 14,
              fontWeight: 500,
              color: HEADER_FG,
              lineHeight: 1.5,
              marginTop: "auto",
            }}
          >
            <span>Hiya Healthcare</span>
            <span />
          </div>
        </div>
      </div>
    </Modal>
  );
}
